import PropTypes from 'prop-types';
import { useRef, useCallback } from 'react';
import Select, { components } from 'react-select';
import { sentenceCase } from '@repo/design-system/components/forms/utils';

import './DropdownSelect.scss';

/**
 * Custom MenuList injected into react-select.
 * Shows the normal option list AND appends a smooth "loading more…" footer
 * when fetching the next page.  The footer does NOT replace the existing
 * options, so the dropdown never flickers or looks broken.
 */
const LoadingFooter = () => (
    <div className="lazy-dropdown-loading-footer">
        <span className="lazy-dropdown-dot-spinner">
            <span />
            <span />
            <span />
        </span>
        <span className="lazy-dropdown-loading-text">Loading more…</span>
    </div>
);

const CustomMenuList = ({ children, ...props }) => {
    const { isLoadingMore } = props.selectProps;
    return (
        <components.MenuList {...props}>
            {children}
            {isLoadingMore && <LoadingFooter />}
        </components.MenuList>
    );
};

/**
 * LazyDropdown — scroll-paginated react-select wrapper.
 *
 * Props that differ from DropdownSelect:
 *   isLoading     — true ONLY on the very first fetch (shows react-select spinner in control)
 *   isLoadingMore — true when appending a subsequent page (shows footer loader below options)
 *   hasMore       — whether more pages exist
 *   onLoadMore    — callback: parent increments page and fetches
 *   onSearch      — callback (debounced 300ms): parent resets + searches
 *
 * DropdownSelect.jsx is NOT modified.
 */
function LazyDropdown({
    value,
    handleDropdownChange,
    handleDropdownBlur,
    optionsValue,
    isMultiSelect,
    isClearable,
    placeholder,
    isDropdownDisabled,
    isSearchable,
    isLoading,
    isLoadingMore,
    hasMore,
    onLoadMore,
    onSearch,
    onMenuOpen,
}) {
    const searchTimerRef = useRef(null);

    // Called by react-select when the menu list is scrolled to the bottom.
    const handleMenuScrollToBottom = useCallback(() => {
        // Guard: don't fire while any fetch (initial OR more) is in flight
        if (hasMore && !isLoading && !isLoadingMore && onLoadMore) {
            onLoadMore();
        }
    }, [hasMore, isLoading, isLoadingMore, onLoadMore]);

    // Called on every keystroke — debounced 300 ms before forwarding to parent.
    const handleInputChange = useCallback(
        (inputValue, { action }) => {
            if (action !== 'input-change') return;
            if (!onSearch) return;
            clearTimeout(searchTimerRef.current);
            searchTimerRef.current = setTimeout(() => {
                onSearch(inputValue);
            }, 300);
        },
        [onSearch]
    );

    return (
        <span className="d-inline-block custom-input">
            <Select
                className="custom-checkbox-select"
                value={value}
                onChange={handleDropdownChange}
                onBlur={handleDropdownBlur}
                onMenuOpen={onMenuOpen}
                options={optionsValue}
                isMulti={isMultiSelect}
                isClearable={isClearable !== undefined ? isClearable : true}
                placeholder={placeholder ? sentenceCase(placeholder) : placeholder}
                isDisabled={isDropdownDisabled}
                isSearchable={isSearchable !== false}
                menuPortalTarget={document.body}
                menuPosition="absolute"
                menuPlacement="auto"
                closeMenuOnSelect={isMultiSelect ? false : true}
                // ── Scroll / search ──────────────────────────────────────────
                onMenuScrollToBottom={handleMenuScrollToBottom}
                onInputChange={onSearch ? handleInputChange : undefined}
                // Server-side search → disable client-side filter
                filterOption={onSearch ? () => true : undefined}
                // ── Loading states ───────────────────────────────────────────
                // react-select's isLoading shows a spinner in the control
                // We use it ONLY for the initial load so the control doesn't
                // flicker on every "load more" fetch.
                isLoading={isLoading}
                loadingMessage={() => 'Loading…'}
                noOptionsMessage={() => (isLoading ? 'Loading…' : 'No options')}
                // ── Custom MenuList footer loader ────────────────────────────
                // isLoadingMore is passed via selectProps and consumed by CustomMenuList
                isLoadingMore={isLoadingMore}
                components={{ MenuList: CustomMenuList }}
                // ────────────────────────────────────────────────────────────
                styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
            />
        </span>
    );
}

LazyDropdown.propTypes = {
    value: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object),
    ]),
    handleDropdownChange: PropTypes.func.isRequired,
    handleDropdownBlur  : PropTypes.func,
    optionsValue        : PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.any.isRequired,
        label: PropTypes.string.isRequired,
    })).isRequired,
    isMultiSelect      : PropTypes.bool,
    placeholder        : PropTypes.string,
    isDropdownDisabled : PropTypes.bool,
    isSearchable       : PropTypes.bool,
    /** True only during the very first page fetch */
    isLoading          : PropTypes.bool,
    /** True while appending subsequent pages — shows footer loader */
    isLoadingMore      : PropTypes.bool,
    hasMore            : PropTypes.bool,
    onLoadMore         : PropTypes.func,
    onSearch           : PropTypes.func,
    onMenuOpen         : PropTypes.func,
};

LazyDropdown.defaultProps = {
    optionsValue : [],
    isLoading    : false,
    isLoadingMore: false,
    hasMore      : false,
};

export default LazyDropdown;
